import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import {
  Pin,
  Archive,
  ArchiveRestore,
  Trash2,
  Eye,
  Edit3,
  Save,
  X,
  Sparkles,
  Link as LinkIcon,
  Plus,
  ExternalLink,
  Loader2,
  ArrowRight,
} from 'lucide-react';
import { COLOR_KEYS, getColor } from './noteColors';
import RichNoteEditor from './RichNoteEditor';
import NoteContentRenderer from './NoteContentRenderer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TAG_REGEX = /[,\s]+/;

const NoteModal = ({
  open,
  onClose,
  onSave,
  onDelete,
  onArchive,
  onUnarchive,
  onTogglePin,
  onConvertToTask,
  initialNote = null,
  categories = [],
  users = [],
  currentUserId,
  token,
  startInEdit = false,
}) => {
  const isNew = !initialNote;
  const [editing, setEditing] = useState(isNew || startInEdit);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('Other');
  const [color, setColor] = useState('yellow');
  const [tagsInput, setTagsInput] = useState('');
  const [isPinned, setIsPinned] = useState(false);
  const [isShared, setIsShared] = useState(false);
  const [assignedTo, setAssignedTo] = useState('');
  const [urlAttachments, setUrlAttachments] = useState([]);
  const [newUrl, setNewUrl] = useState('');
  const [mentionedUserIds, setMentionedUserIds] = useState([]);
  const [saving, setSaving] = useState(false);

  // AI states
  const [aiSummary, setAiSummary] = useState(null);
  const [aiIdeas, setAiIdeas] = useState(null);
  const [aiLoading, setAiLoading] = useState(null); // 'summarize' | 'ideas' | null

  useEffect(() => {
    if (!open) return;
    if (initialNote) {
      setTitle(initialNote.title || '');
      setContent(initialNote.content || '');
      setCategory(initialNote.category || 'Other');
      setColor(initialNote.color || 'yellow');
      setTagsInput((initialNote.tags || []).join(', '));
      setIsPinned(!!initialNote.is_pinned);
      setIsShared(!!initialNote.is_shared);
      setAssignedTo(initialNote.assigned_to || '');
      setUrlAttachments(initialNote.url_attachments || []);
      setMentionedUserIds(initialNote.mentioned_user_ids || []);
      setEditing(startInEdit);
    } else {
      setTitle('');
      setContent('');
      setCategory('Other');
      setColor('yellow');
      setTagsInput('');
      setIsPinned(false);
      setIsShared(false);
      setAssignedTo('');
      setUrlAttachments([]);
      setMentionedUserIds([]);
      setEditing(true);
    }
    setAiSummary(null);
    setAiIdeas(null);
    setAiLoading(null);
  }, [open, initialNote, startInEdit]);

  const isOwner = initialNote ? initialNote.created_by === currentUserId : true;
  const isAssignee = initialNote ? initialNote.assigned_to === currentUserId : false;
  const canEdit = isNew || isOwner || isAssignee;
  const colorPreview = getColor(color);

  const handleSave = async () => {
    if (!title.trim()) return;
    setSaving(true);
    const tags = tagsInput
      .split(TAG_REGEX)
      .map((t) => t.replace(/^#/, '').trim())
      .filter(Boolean);
    const payload = {
      title: title.trim(),
      content,
      category,
      color,
      tags,
      is_pinned: isPinned,
      url_attachments: urlAttachments,
      mentioned_user_ids: mentionedUserIds,
    };
    if (isOwner || isNew) {
      payload.is_shared = isShared;
      payload.assigned_to = assignedTo || null;
    }
    try {
      await onSave(payload, initialNote);
    } finally {
      setSaving(false);
    }
  };

  const addUrl = () => {
    const u = newUrl.trim();
    if (!u) return;
    if (!/^https?:\/\//i.test(u)) {
      toast.error('URL must start with http:// or https://');
      return;
    }
    if (urlAttachments.includes(u)) {
      setNewUrl('');
      return;
    }
    setUrlAttachments([...urlAttachments, u]);
    setNewUrl('');
  };

  const removeUrl = (u) => {
    setUrlAttachments(urlAttachments.filter((x) => x !== u));
  };

  const runSummarize = async () => {
    if (isNew || !initialNote) {
      toast.error('Save the note first to use AI');
      return;
    }
    setAiLoading('summarize');
    setAiSummary(null);
    try {
      const res = await axios.post(
        `${API}/notes/${initialNote.id}/summarize`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAiSummary(res.data.summary);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to summarize');
    } finally {
      setAiLoading(null);
    }
  };

  const runGenerateIdeas = async () => {
    if (isNew || !initialNote) {
      toast.error('Save the note first to use AI');
      return;
    }
    setAiLoading('ideas');
    setAiIdeas(null);
    try {
      const res = await axios.post(
        `${API}/notes/${initialNote.id}/generate-ideas`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAiIdeas(res.data.ideas || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate ideas');
    } finally {
      setAiLoading(null);
    }
  };

  const appendIdeaToContent = (idea) => {
    // Append as an HTML list item so the rich editor parses it cleanly.
    const safe = String(idea).replace(/</g, '&lt;').replace(/>/g, '&gt;');
    setContent((prev) => {
      if (!prev) return `<ul><li>${safe}</li></ul>`;
      // If the current content already ends with a <ul>, slot the new item inside it.
      if (/<\/ul>\s*$/.test(prev)) {
        return prev.replace(/<\/ul>\s*$/, `<li>${safe}</li></ul>`);
      }
      return `${prev}<ul><li>${safe}</li></ul>`;
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className={`max-w-2xl max-h-[92vh] overflow-y-auto ${colorPreview.card} border-2`}
        data-testid="note-modal"
      >
        <DialogHeader className="flex-row items-center justify-between gap-2">
          <DialogTitle className={colorPreview.text}>
            {isNew ? 'New Note' : editing ? 'Edit Note' : 'Note'}
          </DialogTitle>
          <div className="flex items-center gap-1">
            {!isNew && canEdit && !editing && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setEditing(true)}
                data-testid="note-modal-edit-btn"
              >
                <Edit3 size={14} className="mr-1" /> Edit
              </Button>
            )}
            {!isNew && editing && canEdit && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setEditing(false)}
                data-testid="note-modal-preview-btn"
              >
                <Eye size={14} className="mr-1" /> Preview
              </Button>
            )}
          </div>
        </DialogHeader>

        {/* Linked task/post chip */}
        {initialNote?.linked_task_id && (
          <a
            href={`/tasks`}
            className="inline-flex items-center gap-1 self-start text-xs px-2 py-1 rounded-full bg-white/70 text-slate-700 hover:bg-white"
            data-testid="note-linked-task-chip"
          >
            <LinkIcon size={11} /> Linked Task <ArrowRight size={11} />
          </a>
        )}

        {editing && canEdit ? (
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="note-title">Title</Label>
              <Input
                id="note-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="What's on your mind?"
                className="bg-white/60"
                autoFocus
                data-testid="note-title-input"
              />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label>Content (rich text)</Label>
                {!isNew && (
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={runSummarize}
                      disabled={aiLoading === 'summarize'}
                      className="h-7 text-xs"
                      data-testid="ai-summarize-btn"
                    >
                      {aiLoading === 'summarize' ? (
                        <Loader2 className="animate-spin mr-1" size={12} />
                      ) : (
                        <Sparkles size={12} className="mr-1" />
                      )}
                      Summarize
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={runGenerateIdeas}
                      disabled={aiLoading === 'ideas'}
                      className="h-7 text-xs"
                      data-testid="ai-ideas-btn"
                    >
                      {aiLoading === 'ideas' ? (
                        <Loader2 className="animate-spin mr-1" size={12} />
                      ) : (
                        <Sparkles size={12} className="mr-1" />
                      )}
                      Ideas
                    </Button>
                  </div>
                )}
              </div>
              <RichNoteEditor
                value={content}
                onChange={setContent}
                placeholder="Write your thoughts… Format with the toolbar above. Paste from Word/Google Docs is supported."
              />
            </div>

            {/* AI results */}
            {aiSummary && (
              <div
                className="p-3 rounded-lg bg-white/80 border border-amber-300"
                data-testid="ai-summary-result"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-amber-700 flex items-center gap-1">
                    <Sparkles size={12} /> Summary
                  </span>
                  <button onClick={() => setAiSummary(null)} className="text-amber-600">
                    <X size={12} />
                  </button>
                </div>
                <p className="text-sm text-slate-700">{aiSummary}</p>
              </div>
            )}
            {aiIdeas && aiIdeas.length > 0 && (
              <div
                className="p-3 rounded-lg bg-white/80 border border-amber-300"
                data-testid="ai-ideas-result"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-amber-700 flex items-center gap-1">
                    <Sparkles size={12} /> Ideas
                  </span>
                  <button onClick={() => setAiIdeas(null)} className="text-amber-600">
                    <X size={12} />
                  </button>
                </div>
                <ul className="space-y-1">
                  {aiIdeas.map((idea, i) => (
                    <li key={i} className="text-sm text-slate-700 flex items-start gap-2 group">
                      <span className="text-amber-500 font-semibold">{i + 1}.</span>
                      <span className="flex-1">{idea}</span>
                      <button
                        onClick={() => appendIdeaToContent(idea)}
                        className="opacity-0 group-hover:opacity-100 text-xs text-amber-600 hover:text-amber-700"
                        data-testid={`ai-idea-append-${i}`}
                      >
                        + Add
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="bg-white/60" data-testid="note-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.name}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="note-tags">Tags (comma or space separated)</Label>
                <Input
                  id="note-tags"
                  value={tagsInput}
                  onChange={(e) => setTagsInput(e.target.value)}
                  placeholder="e.g. growth, q2, urgent"
                  className="bg-white/60"
                  data-testid="note-tags-input"
                />
              </div>
            </div>

            <div>
              <Label>Color</Label>
              <div className="flex gap-2 mt-2 flex-wrap">
                {COLOR_KEYS.map((k) => {
                  const c = getColor(k);
                  return (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setColor(k)}
                      className={`w-8 h-8 rounded-full ${c.solid} transition-transform ${
                        color === k ? 'ring-2 ring-offset-2 ring-slate-800 scale-110' : 'hover:scale-105'
                      }`}
                      title={c.name}
                      data-testid={`note-color-${k}`}
                    />
                  );
                })}
              </div>
            </div>

            {/* URL attachments */}
            <div>
              <Label>URL Attachments</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addUrl();
                    }
                  }}
                  placeholder="https://..."
                  className="bg-white/60"
                  data-testid="note-url-input"
                />
                <Button type="button" size="sm" onClick={addUrl} data-testid="note-url-add">
                  <Plus size={14} />
                </Button>
              </div>
              {urlAttachments.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {urlAttachments.map((u) => (
                    <span
                      key={u}
                      className="inline-flex items-center gap-1 text-xs bg-white/70 px-2 py-1 rounded-full"
                      data-testid={`url-chip-${u}`}
                    >
                      <LinkIcon size={10} />
                      <span className="max-w-[200px] truncate">{u}</span>
                      <button
                        type="button"
                        onClick={() => removeUrl(u)}
                        className="ml-1 hover:text-red-600"
                      >
                        <X size={10} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {(isNew || isOwner) && (
              <div className="space-y-3 pt-2 border-t border-slate-300/50">
                <div className="flex items-center justify-between">
                  <Label htmlFor="note-pinned" className="cursor-pointer">
                    Pin to top
                  </Label>
                  <Switch
                    id="note-pinned"
                    checked={isPinned}
                    onCheckedChange={setIsPinned}
                    data-testid="note-pinned-switch"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="note-shared" className="cursor-pointer">
                      Share with team
                    </Label>
                    <p className="text-xs opacity-70">All teammates will see this note.</p>
                  </div>
                  <Switch
                    id="note-shared"
                    checked={isShared}
                    onCheckedChange={setIsShared}
                    data-testid="note-shared-switch"
                  />
                </div>

                <div>
                  <Label>Assign to (optional)</Label>
                  <Select
                    value={assignedTo || 'none'}
                    onValueChange={(v) => setAssignedTo(v === 'none' ? '' : v)}
                  >
                    <SelectTrigger className="bg-white/60" data-testid="note-assignee-select">
                      <SelectValue placeholder="No one" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No one</SelectItem>
                      {users
                        .filter((u) => u.id !== currentUserId)
                        .map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.full_name || u.email}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3 py-2">
            <h2 className={`text-2xl font-bold ${colorPreview.text}`}>
              {title || 'Untitled'}
            </h2>
            <div className={`flex flex-wrap items-center gap-2 text-xs ${colorPreview.text} opacity-80`}>
              <span className="font-semibold">{category}</span>
              {(tagsInput ? tagsInput.split(TAG_REGEX) : []).filter(Boolean).map((t) => (
                <span key={t} className="px-2 py-0.5 rounded-full bg-white/60">
                  #{t.replace(/^#/, '')}
                </span>
              ))}
            </div>
            <div
              className={`${colorPreview.text} bg-white/40 rounded-lg p-4 min-h-[120px]`}
            >
              {content ? (
                <NoteContentRenderer content={content} className={colorPreview.text} />
              ) : (
                <p className="opacity-50 italic">No content</p>
              )}
            </div>
            {urlAttachments.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {urlAttachments.map((u) => (
                  <a
                    key={u}
                    href={u}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs bg-white/70 px-2 py-1 rounded-full hover:bg-white"
                    data-testid={`url-chip-view-${u}`}
                  >
                    <ExternalLink size={10} />
                    <span className="max-w-[220px] truncate">{u}</span>
                  </a>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Footer actions */}
        <div className="flex items-center justify-between pt-3 border-t border-slate-300/50 flex-wrap gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            {!isNew && canEdit && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onTogglePin && onTogglePin(initialNote)}
                data-testid="note-modal-pin-btn"
              >
                <Pin
                  size={14}
                  className={`mr-1 ${initialNote?.is_pinned ? 'fill-current' : ''}`}
                />
                {initialNote?.is_pinned ? 'Unpin' : 'Pin'}
              </Button>
            )}
            {!isNew && canEdit && (
              <Button
                size="sm"
                variant="ghost"
                className="text-amber-700 hover:text-amber-800 hover:bg-amber-50"
                onClick={() => onConvertToTask && onConvertToTask(initialNote)}
                data-testid="note-modal-convert-btn"
              >
                <Sparkles size={14} className="mr-1" /> Convert to Task
              </Button>
            )}
            {!isNew && isOwner && !initialNote?.is_archived && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onArchive && onArchive(initialNote)}
                data-testid="note-modal-archive-btn"
              >
                <Archive size={14} className="mr-1" /> Archive
              </Button>
            )}
            {!isNew && isOwner && initialNote?.is_archived && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onUnarchive && onUnarchive(initialNote)}
                data-testid="note-modal-unarchive-btn"
              >
                <ArchiveRestore size={14} className="mr-1" /> Restore
              </Button>
            )}
            {!isNew && isOwner && (
              <Button
                size="sm"
                variant="ghost"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => onDelete && onDelete(initialNote)}
                data-testid="note-modal-delete-btn"
              >
                <Trash2 size={14} className="mr-1" /> Delete
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={onClose} data-testid="note-modal-close">
              <X size={14} className="mr-1" /> Close
            </Button>
            {editing && canEdit && (
              <Button
                onClick={handleSave}
                disabled={saving || !title.trim()}
                className="btn-cta"
                data-testid="note-modal-save"
              >
                <Save size={14} className="mr-1" />
                {saving ? 'Saving...' : isNew ? 'Create' : 'Save'}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NoteModal;
